<template>
  <div class="card border border-warning border-2 bg-warning bg-opacity-10 shadow-sm">
    <div class="card-header bg-warning bg-opacity-25 border-bottom border-warning">
      <h5 class="fw-bold mb-0 text-dark">
        <i class="fas fa-pause-circle me-2"></i>Backtest Pause
      </h5>
    </div>

    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-warning" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <template v-else>
        <!-- Not a backtest stack -->
        <div v-if="!isBacktest" class="alert alert-secondary mb-0">
          <i class="fas fa-info-circle me-2"></i>
          This stack is not running a backtest, so there is nothing to pause. The control
          is only available on the backtest deployment.
        </div>

        <template v-else>
          <!-- Status badge -->
          <div class="mb-3">
            <span v-if="isPaused" class="badge bg-warning text-dark fs-6 px-3 py-2">
              <i class="fas fa-pause-circle me-2"></i>PAUSED — Replay Frozen
            </span>
            <span v-else-if="isPending" class="badge bg-secondary fs-6 px-3 py-2">
              <span class="spinner-border spinner-border-sm me-2"></span>{{ pendingLabel }}
            </span>
            <span v-else class="badge bg-success fs-6 px-3 py-2">
              <i class="fas fa-play-circle me-2"></i>RUNNING — Replay In Progress
            </span>
          </div>

          <!-- Pause details -->
          <div v-if="isPaused && state" class="mb-3 small text-muted">
            <div><i class="fas fa-clock me-1"></i>Virtual clock held at: <strong>{{ formatEt(state.virtual_utc) }}</strong></div>
            <div><i class="fas fa-hourglass-half me-1"></i>Paused since: {{ formatLocal(state.paused_at_utc) }}</div>
            <div>
              <i class="fas fa-cubes me-1"></i>Containers paused:
              <strong>{{ (state.containers_paused || []).length }}</strong>
              <span v-if="(state.containers_paused || []).length" class="ms-1">({{ state.containers_paused.join(', ') }})</span>
            </div>
          </div>

          <div v-if="isPaused && state && state.docker_available === false" class="alert alert-danger py-2">
            <i class="fas fa-exclamation-triangle me-2"></i>
            The driver could not reach Docker, so only the virtual clock is frozen — the trading
            containers are still running and can still reach the Jetsons. Mount the Docker socket
            into <code>backtest-driver</code> for a full pause.
          </div>
          <div v-else-if="isPaused && state && (state.containers_failed || []).length" class="alert alert-danger py-2">
            <i class="fas fa-exclamation-triangle me-2"></i>
            {{ state.containers_failed.length }} container(s) refused to pause and keep running:
            {{ state.containers_failed.join(', ') }}
          </div>

          <div v-if="!isPaused && lastPause" class="mb-3 small text-muted">
            <i class="fas fa-history me-1"></i>Last pause: {{ formatLocal(lastPause.paused_at_utc) }}
            for {{ formatDuration(lastPause.paused_real_seconds) }},
            virtual clock held at {{ formatEt(lastPause.virtual_utc) }}.
            <span v-if="(lastPause.containers_unpause_failed || []).length" class="text-danger">
              {{ lastPause.containers_unpause_failed.length }} container(s) could not be unpaused:
              {{ lastPause.containers_unpause_failed.join(', ') }}.
            </span>
          </div>

          <div v-if="error" class="alert alert-danger py-2">
            <i class="fas fa-exclamation-circle me-2"></i>{{ error }}
          </div>

          <!-- Actions -->
          <div class="d-flex gap-2 flex-wrap">
            <button
              v-if="!requested"
              class="btn btn-warning"
              :disabled="actionLoading"
              @click="pause"
            >
              <span v-if="actionLoading" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fas fa-pause me-2"></i>Pause Backtest
            </button>
            <button
              v-else
              class="btn btn-success"
              :disabled="actionLoading"
              @click="resume"
            >
              <span v-if="actionLoading" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fas fa-play me-2"></i>Resume Backtest
            </button>
          </div>

          <p class="small text-muted mt-3 mb-0">
            Pausing freezes the virtual clock and docker-pauses the trader, researcher, watcher
            orchestrator, selection scheduler and worker, and every backtest watcher, so the replay
            makes no LLM or market-data calls while the live session is trading. Resuming
            continues the replay from the frozen instant; the paused time is not counted against
            the session.
          </p>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { getBacktestPauseStatus, pauseBacktest, resumeBacktest } from '../services/backtestApi.js';

// The driver acknowledges a request within a tick or two; poll while the page is
// open so the badge flips from "requested" to "paused" without a reload.
const POLL_MS = 3000;

export default {
  name: 'BacktestPausePanel',

  emits: ['status-changed'],

  data() {
    return {
      loading: true,
      actionLoading: false,
      error: null,
      isBacktest: false,
      requested: false,
      isPaused: false,
      state: null,
      pollTimer: null,
    };
  },

  computed: {
    // A request the driver has not acted on yet (pause asked but not yet
    // paused, or resume asked but still paused).
    isPending() {
      return this.requested !== this.isPaused;
    },
    pendingLabel() {
      return this.requested ? 'PAUSE REQUESTED — waiting for the driver' : 'RESUME REQUESTED — waiting for the driver';
    },
    lastPause() {
      return this.state && this.state.last_pause ? this.state.last_pause : null;
    },
  },

  async mounted() {
    await this.loadStatus(true);
    this.pollTimer = setInterval(() => this.loadStatus(false), POLL_MS);
  },

  beforeUnmount() {
    clearInterval(this.pollTimer);
  },

  methods: {
    applyStatus(data) {
      this.isBacktest = data.backtest === true;
      this.requested = data.requested === true;
      this.isPaused = data.paused === true;
      this.state = data.state || null;
    },

    async loadStatus(initial) {
      if (initial) this.loading = true;
      try {
        const data = await getBacktestPauseStatus();
        this.applyStatus(data);
        if (!initial) this.error = null;
      } catch (err) {
        this.error = err.message;
        console.error('[BacktestPausePanel] Failed to load status:', err);
      } finally {
        if (initial) this.loading = false;
      }
    },

    async pause() {
      this.actionLoading = true;
      this.error = null;
      try {
        console.log('[BacktestPausePanel] Requesting pause.');
        this.applyStatus(await pauseBacktest());
        this.$emit('status-changed', true);
      } catch (err) {
        this.error = err.message;
        console.error('[BacktestPausePanel] Failed to request pause:', err);
      } finally {
        this.actionLoading = false;
      }
    },

    async resume() {
      this.actionLoading = true;
      this.error = null;
      try {
        console.log('[BacktestPausePanel] Requesting resume.');
        this.applyStatus(await resumeBacktest());
        this.$emit('status-changed', false);
      } catch (err) {
        this.error = err.message;
        console.error('[BacktestPausePanel] Failed to request resume:', err);
      } finally {
        this.actionLoading = false;
      }
    },

    formatLocal(ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    },

    formatEt(ts) {
      if (!ts) return '';
      return `${new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
      }).format(new Date(ts))} ET`;
    },

    formatDuration(seconds) {
      const s = Number(seconds) || 0;
      if (s < 60) return `${Math.round(s)}s`;
      const m = Math.floor(s / 60);
      if (m < 60) return `${m}m ${Math.round(s % 60)}s`;
      return `${Math.floor(m / 60)}h ${m % 60}m`;
    },
  },
};
</script>
